import { Suspense } from "react";
import { Navigate, Outlet, type RouteObject } from "react-router";
import { type UserInfo, useUserInfo } from "../api/userInfo";
import {
  SystemIcon3,
  DpiaIcon3,
  RegistrationControllerIcon3,
  LayoutDashboardIcon,
  PartnerIcon3,
  UploadIcon,
  ClipboardListIcon,
  OptionCategoryIcon3,
  UsersIcon,
  InformationTypeIcon3,
  SparklesIcon,
} from "../shared/icons";
import { useAuthUser } from "../auth/AuthProvider";
import { InitSnowplow, trackEvent } from "../shared/Snowplow";
import { appConfig } from "../appConfig";
import { Layout, LayoutWithoutSelectedOrganisation } from "./layout/Layout";
import { useGetHasAccessArgs, type NavigationItemType, type SidebarMenuItemType } from "./layout/SidebarMenuItem";
import { HelpMenuAccordion } from "./layout/HelpMenuAccordion";
import { useSyncTranslationConfig } from "@privacy/translations";
import * as registrationRoute from "../pages/RegistrationsGridPage/route";
import * as dpiaRoute from "../pages/DpiaGridPage/route";
import * as measuresRoute from "../pages/MeasureGridPage/route";
import * as risksRoute from "../pages/RiskGridPage/route";
import * as systemsRoute from "../pages/SystemsGridPage/route";
import { prefetch } from "../api/prefetch";
import { optionCategoryPageRoute } from "../optionCategoryList/OptionCategoryPage";
import { organisationOptionCategoryScope } from "../optionCategoryList/optionCategoryScopes";
import { OPTION_CATEGORY_TYPE_CONFIG, OPTION_CATEGORY_TYPES } from "../optionCategoryList/optionCategoryTypeConfig";
import * as partnersRoute from "../pages/PartnersGridPage/route";
import * as informationTypesRoute from "../pages/InformationTypesGridPage/route";
import * as userGridRoute from "../pages/SettingsPage.tsx/UserGridPage/route";
import * as personalDataControllersRoute from "../pages/SettingsPage.tsx/PersonalDataControllersGridPage/route";
import * as organisationUnitsRoute from "../pages/SettingsPage.tsx/OrganisationUnitsPage/route";
import * as organisationStructureRoute from "../pages/SettingsPage.tsx/OrganisationStructurePage/route";
import * as scimMappingRoute from "../pages/SettingsPage.tsx/ScimMappingPage/route";
import * as templateRoutes from "../pages/SettingsPage.tsx/TemplatePage/route";
import * as dataImportPageRoute from "../pages/DataImportPage/route";
import * as dashboardRoute from "../pages/DashboardPage/route";
import * as assistantRoute from "../pages/AssistantPage/route";
import { hasAssistantAccess } from "../shared/assistantAccess";
import { AuthenticatedPageErrorBoundary, ErrorNotPermitted } from "./error/ErrorPage";
import { PageSpinner } from "../shared/Page";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { CurrentOrganisationProvider, useCurrentCustomer, useCurrentOrganisation } from "./CurrentOrganisationProvider";
import { enterpriseCustomerNavigationItems } from "../enterpriseCustomer/enterpriseCustomerNavigationItems";
import { accessibleOrganisations, isEnterpriseCustomerScope } from "../enterpriseCustomer/enterpriseCustomer";
import { EnterpriseCustomerSidebarHeader } from "../enterpriseCustomer/EnterpriseCustomerSidebarHeader";
import { SystemAnnouncementBanner } from "../shared/SystemAnnouncementBanner";
import { InformationTypesUpsellChip, InformationTypesUpsellDot } from "../pages/InformationTypesGridPage/NewChip";

export const OrganisationSelectedProviders = () => {
  return (
    <CurrentOrganisationProvider>
      <OrganisationSelectedProvidersWithUserInfo />
    </CurrentOrganisationProvider>
  );
};

const OrganisationSelectedProvidersWithUserInfo = () => {
  const userInfo = useUserInfo();
  const currentOrganisation = useCurrentOrganisation();
  const currentCustomer = useCurrentCustomer();

  useSyncTranslationConfig({
    isE2eUser: userInfo.isE2EUser,
    language: currentOrganisation.lang,
  });

  if (currentCustomer.customerKind === "enterprise") {
    const organisations = accessibleOrganisations(currentCustomer, currentOrganisation.lang);

    if (!organisations.some(({ id }) => id === currentOrganisation.id)) {
      const [firstOrganisation] = organisations;

      return firstOrganisation ? (
        <Navigate to={`/${firstOrganisation.slug}`} replace />
      ) : (
        <LayoutWithoutSelectedOrganisation>
          <ErrorNotPermitted isAuthenticated={true} />
        </LayoutWithoutSelectedOrganisation>
      );
    }
  }

  return (
    <>
      <Tracker userInfo={userInfo} />

      <Layout
        sidebarHeader={
          currentCustomer.customerKind === "enterprise" ? (
            <EnterpriseCustomerSidebarHeader customer={currentCustomer} />
          ) : undefined
        }
        menuItemEmphasis={currentCustomer.customerKind === "enterprise" ? "active-item-only" : "all-items"}
        sidebarMenuItems={
          isEnterpriseCustomerScope({ currentCustomer, currentOrganisation })
            ? enterpriseCustomerNavigationItems
            : sidebarMenuItems
        }
      >
        <SystemAnnouncementBanner systemAnnouncement={currentOrganisation.systemAnnouncement} />
        <AuthenticatedPageErrorBoundary>
          <Suspense fallback={<PageSpinner />}>
            <Outlet />
          </Suspense>
        </AuthenticatedPageErrorBoundary>
      </Layout>
    </>
  );
};

const Tracker = ({ userInfo }: { userInfo: UserInfo }) => {
  const currentOrganisation = useCurrentOrganisation();
  const authUser = useAuthUser();

  const disableTracking =
    appConfig.env.APP_ENV !== "prod" ||
    currentOrganisation.isTestOrganization ||
    Boolean(userInfo.isInternalUser) ||
    Boolean(userInfo.isE2EUser);

  if (disableTracking) return null;

  return (
    <InitSnowplow organisation={currentOrganisation} user={{ createdAt: authUser.createdAt, email: authUser.email }} />
  );
};

const sidebarMenuItems: SidebarMenuItemType[] = [
  {
    variant: "navigationItem",
    hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:dashboard:read"),
    Icon: LayoutDashboardIcon,
    labelKey: "Sidebar.Dashboard",
    to: dashboardRoute.to,
    route: dashboardRoute.route,
  },
  {
    variant: "navigationItem",
    hasAccess: hasAssistantAccess,
    Icon: SparklesIcon,
    labelKey: "Sidebar.Assistant",
    to: assistantRoute.to,
    route: assistantRoute.route,
    onHover: assistantRoute.prefetch,
  },
  {
    variant: "accordionMenu",
    labelKey: "Sidebar.Registrations",
    Icon: RegistrationControllerIcon3,
    children: [
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:registration:read"),
        labelKey: "Sidebar.RegistrationsController",
        to: registrationRoute.to("Controller"),
        route: registrationRoute.route({ variant: "Controller", path: "registrations" }),
        onHover: () => registrationRoute.prefetch("Controller"),
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:registration:read"),
        labelKey: "Sidebar.RegistrationsProcessor",
        to: registrationRoute.to("Processor"),
        route: registrationRoute.route({ variant: "Processor", path: "registrations-processor" }),
        onHover: () => registrationRoute.prefetch("Processor"),
      },
    ],
  },
  {
    variant: "accordionMenu",
    labelKey: "Sidebar.Dpia",
    Icon: DpiaIcon3,
    children: [
      {
        variant: "navigationItem",
        labelKey: "Sidebar.AllDpia",
        hasAccess: ({ hasPermission, currentOrganisation }) =>
          currentOrganisation.dpiaEnabled && hasPermission("nova-forms-api:dpia:gridview"),
        to: dpiaRoute.to,
        route: dpiaRoute.route,
        onHover: dpiaRoute.prefetch,
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission, currentOrganisation }) =>
          currentOrganisation.dpiaEnabled && hasPermission("nova-forms-api:measure:gridview"), // TODO: Add permission for risks gridview
        labelKey: "Sidebar.Risks",
        route: risksRoute.route,
        to: risksRoute.to,
        onHover: risksRoute.prefetch,
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission, currentOrganisation }) =>
          currentOrganisation.dpiaEnabled && hasPermission("nova-forms-api:measure:gridview"),
        labelKey: "Sidebar.Measures",
        to: measuresRoute.to,
        route: measuresRoute.route,
        onHover: measuresRoute.prefetch,
      },
    ],
  },
  {
    variant: "navigationItem",
    hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:system:gridview"),
    Icon: SystemIcon3,
    labelKey: "Sidebar.Systems",
    to: systemsRoute.to,
    route: systemsRoute.route,
    onHover: systemsRoute.prefetch,
  },
  {
    variant: "navigationItem",
    hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:partner:gridview"),
    Icon: PartnerIcon3,
    labelKey: "Sidebar.Partners",
    to: partnersRoute.to,
    route: partnersRoute.route,
    onHover: partnersRoute.prefetch,
  },
  {
    variant: "navigationItem",
    hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:information-type:gridview"),
    renderEndIcon: () => <InformationTypesUpsellChip />,
    renderCollapsedBadge: () => <InformationTypesUpsellDot />,
    Icon: InformationTypeIcon3,
    labelKey: "InformationType.Sidebar.InformationTypes",
    to: informationTypesRoute.to,
    route: informationTypesRoute.route,
    onHover: informationTypesRoute.prefetch,
    onClick: ({ currentOrganisation }) => {
      if (!currentOrganisation.classificationEnabled) trackEvent.clickPrimaryAssetsSidebarItemWithoutFeatureEnabled();
    },
  },
  {
    variant: "accordionMenu",
    labelKey: "Sidebar.Categories",
    Icon: OptionCategoryIcon3,
    children: OPTION_CATEGORY_TYPES.map(categoryType => ({
      variant: "navigationItem",
      hasAccess: ({ hasPermission }) => hasPermission(organisationOptionCategoryScope.permission(categoryType, "read")),
      labelKey: OPTION_CATEGORY_TYPE_CONFIG[categoryType].textKeys.title,
      to: paths => paths.optionCategoryList({ categoryType }),
      route: optionCategoryPageRoute(organisationOptionCategoryScope, categoryType),
      onHover: () => prefetch(organisationOptionCategoryScope.listQueryOptions(categoryType)),
    })),
  },
  {
    variant: "accordionMenu",
    labelKey: "Sidebar.Settings",
    Icon: UsersIcon,
    children: [
      {
        variant: "navigationItem",
        labelKey: "Sidebar.People",
        to: userGridRoute.to,
        hasAccess: ({ hasPermission }) => hasPermission("organization-api:user:roles:update:productLevel"),
        route: userGridRoute.route,
        onHover: userGridRoute.prefetch,
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission }) =>
          hasPermission("organization-api:tenant:hierarchy:read") && hasPermission("organization-api:user:roles:read"),
        route: organisationUnitsRoute.route,
        to: organisationUnitsRoute.to,
        labelKey: "Sidebar.Tenants",
        onHover: organisationUnitsRoute.prefetch,
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission }) =>
          hasPermission("organization-api:tenant:hierarchy:read") &&
          hasPermission("organization-api:tenant:hierarchy:update"),
        route: organisationStructureRoute.route,
        to: organisationStructureRoute.to,
        labelKey: "Sidebar.OrganisationTree",
        onHover: organisationStructureRoute.prefetch,
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission, currentOrganisation }) =>
          currentOrganisation.scimEnabled && hasPermission("organization-api:scim:read"),
        route: scimMappingRoute.route,
        to: scimMappingRoute.to,
        labelKey: "Sidebar.ScimMapping",
        onHover: scimMappingRoute.prefetch,
      },
      {
        variant: "navigationItem",
        hasAccess: ({ hasPermission, currentCustomer }) =>
          currentCustomer.customerKind !== "enterprise" && hasPermission("nova-forms-api:pdc:settings"),
        labelKey: "Sidebar.PersonalDataControllers",
        to: personalDataControllersRoute.to,
        route: personalDataControllersRoute.route,
        onHover: personalDataControllersRoute.prefetch,
      },
    ],
  },
  {
    variant: "navigationItem",
    hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:organisation:form:settings:read"),
    Icon: ClipboardListIcon,
    route: templateRoutes.route,
    to: templateRoutes.to,
    labelKey: "Sidebar.Templates",
    onHover: templateRoutes.prefetch,
  },
  {
    variant: "navigationItem",
    hasAccess: ({ hasPermission }) => hasPermission("nova-forms-api:data-import"),
    Icon: UploadIcon,
    route: dataImportPageRoute.route,
    to: dataImportPageRoute.to,
    labelKey: "Sidebar.DataImport",
  },

  { variant: "custom", id: "helpMenuAccordion", element: <HelpMenuAccordion /> },
];

const WithAccess = ({ hasAccess }: { hasAccess: NavigationItemType["hasAccess"] }) => {
  const hasAccessArgs = useGetHasAccessArgs();
  if (!hasAccess(hasAccessArgs)) return <ErrorNotPermitted isAuthenticated={true} />;
  return <Outlet />;
};

const extractNavigationItems = (sidebarMenuItems: SidebarMenuItemType[]): NavigationItemType[] => {
  return sidebarMenuItems
    .flatMap(item => (item.variant === "accordionMenu" ? item.children : [item]))
    .filter((item): item is NavigationItemType => item.variant === "navigationItem");
};

export const extractRoutesFromSidebarMenuItems = (): RouteObject[] => {
  return [
    { index: true, element: <NavigateToFirstAccessibleRoute /> },
    ...extractNavigationItems([...sidebarMenuItems, ...enterpriseCustomerNavigationItems]).map(
      ({ route, hasAccess }) => ({
        element: <WithAccess hasAccess={hasAccess} />,
        children: [route],
      })
    ),
  ];
};

const NavigateToFirstAccessibleRoute = () => {
  const hasAccessArgs = useGetHasAccessArgs();
  const { currentCustomer, currentOrganisation } = hasAccessArgs;
  const { paths } = usePrivacyNavigate();
  const navigationItems = extractNavigationItems(
    isEnterpriseCustomerScope({ currentCustomer, currentOrganisation })
      ? enterpriseCustomerNavigationItems
      : sidebarMenuItems
  );
  const firstAccessibleItem = navigationItems.find(({ hasAccess }) => hasAccess(hasAccessArgs));
  return firstAccessibleItem ? (
    <Navigate to={firstAccessibleItem.to(paths)} replace />
  ) : (
    <ErrorNotPermitted isAuthenticated={true} />
  );
};
